/**
 * useSSE Hook - Manages Server-Sent Events connection
 * Single Responsibility: Only handles SSE connection lifecycle
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const SSE_RECONNECT_DELAY = 5000; // 5 seconds (increased)
const SSE_MAX_RETRIES = 3; // Reduced max retries

export const SSE_EVENTS = {
  // Notification events
  NOTIFICATION_NEW: 'notification:new',
  NOTIFICATION_READ: 'notification:read',
  NOTIFICATION_DELETED: 'notification:deleted',
  
  // Trip events
  TRIP_MEMBER_JOINED: 'trip:member_joined',
  TRIP_MEMBER_LEFT: 'trip:member_left',
  TRIP_MEMBER_APPROVED: 'trip:member_approved',
  TRIP_MEMBER_REJECTED: 'trip:member_rejected',
  TRIP_UPDATED: 'trip:updated',
  
  // Join request events
  JOIN_REQUEST_NEW: 'join_request:new',
  JOIN_REQUEST_APPROVED: 'join_request:approved',
  JOIN_REQUEST_REJECTED: 'join_request:rejected',
  
  // Payment events
  PAYMENT_CREATED: 'payment:created',
  PAYMENT_RECEIVED: 'payment:received',
  PAYMENT_REMINDER: 'payment:reminder',
  
  // System events
  SYSTEM_PING: 'system:ping',
  SYSTEM_CONNECTED: 'system:connected',
};

/**
 * Custom hook for SSE connection
 * @param {string} token - JWT token for authentication
 * @param {Object} handlers - Event handlers { [eventType]: (data) => void }
 */
export function useSSE(token, handlers = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  
  const handlersRef = useRef(handlers);
  const reconnectTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const abortControllerRef = useRef(null);
  const isConnectingRef = useRef(false);
  
  // Update handlers ref when handlers change
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);
  
  const connect = useCallback(() => {
    if (!token) {
      return;
    }
    
    // Prevent multiple simultaneous connections
    if (isConnectingRef.current) {
      return;
    }
    
    isConnectingRef.current = true;
    
    // Abort existing connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const sseUrl = `${API_URL}/api/sse/notifications`;
    
    const fetchSSE = async () => {
      try {
        const response = await fetch(sseUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
          signal: abortControllerRef.current.signal,
        });
        
        if (!response.ok) {
          throw new Error(`SSE connection failed: ${response.status}`);
        }
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        setIsConnected(true);
        setConnectionError(null);
        retryCountRef.current = 0;
        isConnectingRef.current = false;
        
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }
          
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete events (double newline separated)
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';
          
          for (const eventStr of events) {
            if (!eventStr.trim()) continue;
            
            try {
              const lines = eventStr.split('\n');
              let eventType = 'message';
              let eventData = '';
              
              for (const line of lines) {
                if (line.startsWith('event: ')) {
                  eventType = line.slice(7);
                } else if (line.startsWith('data: ')) {
                  eventData = line.slice(6);
                }
              }
              
              if (eventData) {
                const data = JSON.parse(eventData);
                
                if (handlersRef.current[eventType]) {
                  handlersRef.current[eventType](data);
                }
                
                if (handlersRef.current.onEvent) {
                  handlersRef.current.onEvent(eventType, data);
                }
              }
            } catch (parseError) {
              // Silently ignore parse errors
            }
          }
        }
      } catch (error) {
        isConnectingRef.current = false;
        
        // Ignore abort errors
        if (error.name === 'AbortError') {
          return;
        }
        
        setIsConnected(false);
        setConnectionError(error.message);
        
        // Attempt reconnection with exponential backoff
        if (retryCountRef.current < SSE_MAX_RETRIES) {
          retryCountRef.current += 1;
          const delay = SSE_RECONNECT_DELAY * retryCountRef.current;
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      }
    };
    
    fetchSSE();
  }, [token]);
  
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    isConnectingRef.current = false;
    setIsConnected(false);
  }, []);
  
  // Connect when token is available
  useEffect(() => {
    if (token) {
      // Small delay to prevent rapid reconnections
      const timeoutId = setTimeout(() => {
        connect();
      }, 500);
      
      return () => {
        clearTimeout(timeoutId);
        disconnect();
      };
    }
    
    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);
  
  // Handle page visibility - reconnect when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && token && !isConnected && !isConnectingRef.current) {
        retryCountRef.current = 0;
        connect();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, isConnected, connect]);
  
  return {
    isConnected,
    connectionError,
    reconnect: connect,
    disconnect,
  };
}

export default useSSE;
